import type { OperatorRole } from './auth';
import { getOperator } from './auth';

export type OperatorPermissionFlags = {
  canViewUsage: boolean;
  canViewBilling: boolean;
  canReconcilePayment: boolean;
  canViewModels: boolean;
  canEditModels: boolean;
  canManageAgents: boolean;
  canRevokeSessions: boolean;
  canPatchUser: boolean;
  canViewOperatorAudit: boolean;
  canViewSecurityAudit: boolean;
  canManageTeam: boolean;
  canInstagramRedact: boolean;
  canViewInstagram: boolean;
  canViewWorkspaces: boolean;
  canViewTelegramBusiness: boolean;
  canManageEconomics: boolean;
};

function role(): OperatorRole | null {
  return getOperator()?.role ?? null;
}

function hasRoleFor(current: OperatorRole | null, ...roles: OperatorRole[]): boolean {
  return current != null && roles.includes(current);
}

export function evaluateOperatorPermissions(
  currentRole: OperatorRole | null,
): OperatorPermissionFlags {
  return {
    canViewUsage: hasRoleFor(currentRole, 'READONLY', 'BILLING', 'SUPER_ADMIN'),
    canViewBilling: hasRoleFor(currentRole, 'READONLY', 'BILLING', 'SUPER_ADMIN'),
    canReconcilePayment: hasRoleFor(currentRole, 'BILLING', 'SUPER_ADMIN'),
    canViewModels: hasRoleFor(currentRole, 'READONLY', 'SUPER_ADMIN'),
    canEditModels: hasRoleFor(currentRole, 'SUPER_ADMIN'),
    canManageAgents: hasRoleFor(currentRole, 'SUPPORT', 'SUPER_ADMIN'),
    canRevokeSessions: hasRoleFor(currentRole, 'SUPPORT', 'SUPER_ADMIN'),
    canPatchUser: hasRoleFor(currentRole, 'SUPPORT', 'SUPER_ADMIN'),
    canViewOperatorAudit: hasRoleFor(currentRole, 'READONLY', 'SUPER_ADMIN'),
    canViewSecurityAudit: hasRoleFor(currentRole, 'READONLY', 'SUPPORT', 'SUPER_ADMIN'),
    canManageTeam: hasRoleFor(currentRole, 'SUPER_ADMIN'),
    canInstagramRedact: hasRoleFor(currentRole, 'SUPER_ADMIN'),
    canViewInstagram: hasRoleFor(currentRole, 'READONLY', 'SUPPORT', 'SUPER_ADMIN'),
    canViewWorkspaces: hasRoleFor(currentRole, 'READONLY', 'SUPER_ADMIN'),
    canViewTelegramBusiness: hasRoleFor(
      currentRole,
      'READONLY',
      'SUPPORT',
      'BILLING',
      'SUPER_ADMIN',
    ),
    canManageEconomics: hasRoleFor(currentRole, 'SUPER_ADMIN'),
  };
}

function flags(): OperatorPermissionFlags {
  return evaluateOperatorPermissions(role());
}

export function canViewUsage(): boolean {
  return flags().canViewUsage;
}

export function canViewBilling(): boolean {
  return flags().canViewBilling;
}

export function canReconcilePayment(): boolean {
  return flags().canReconcilePayment;
}

export function canViewModels(): boolean {
  return flags().canViewModels;
}

export function canEditModels(): boolean {
  return flags().canEditModels;
}

export function canManageAgents(): boolean {
  return flags().canManageAgents;
}

export function canRevokeSessions(): boolean {
  return flags().canRevokeSessions;
}

export function canPatchUser(): boolean {
  return flags().canPatchUser;
}

export function canViewOperatorAudit(): boolean {
  return flags().canViewOperatorAudit;
}

export function canViewSecurityAudit(): boolean {
  return flags().canViewSecurityAudit;
}

export function canManageTeam(): boolean {
  return flags().canManageTeam;
}

export function canInstagramRedact(): boolean {
  return flags().canInstagramRedact;
}

export function canViewInstagram(): boolean {
  return flags().canViewInstagram;
}

export function canViewWorkspaces(): boolean {
  return flags().canViewWorkspaces;
}

export function canViewTelegramBusiness(): boolean {
  return flags().canViewTelegramBusiness;
}

export function canManageEconomics(): boolean {
  return flags().canManageEconomics;
}

export const PERMISSION_DENIED_HINT = 'Недостаточно прав для этого действия';
